import React from 'react';
import { highlightedProductAdderSelector, clearHighlightedProductAdder } from '@src/stores/product';
import { productUpsellModalSelector } from '@src/stores/productUpsell';
import { multiProductBookingSelector, isAddingToCartSelector } from '@src/stores/multiProductBooking';
import { cartStateSelector, closeCartModal } from '@src/features/cart/store/cart';
import IdProvider from '@src/core/IdProvider';
import { WidgetParamContext, useAppSelector, useAppDispatch } from '@src/core/hooks';
import { CartDetails } from '@src/features/cart';
import { BackdropModal } from '@src/components/reusable';
import {
  createdAccountSelector,
  modifiedPasswordSelector,
  resendEmailSelector,
  setCreatedAccount,
  setmodifiedPassword,
  buyerNeedsPhoneVerificationSelector,
} from '@src/features/customers';
import { termsSelector, currentTermsSelector } from '@src/stores/app';
import { TermsModal } from '@src/features/terms';
import SuccessModal from '@src/features/customers/auth/legacy/modals/SuccessModal';
import ResendEmailModal from '@src/features/customers/auth/legacy/modals/ResendEmailModal';
import useTranslate from '../../i18n/useTranslate';
import { DisapprovedPurchaseModal, GatewayPaymentErrorModal, usePaymentErrors } from '@src/features/payments';
import { isRequestTimeoutSelector, setRequestTimeout } from '@src/stores/ui';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { TimeoutModal } from '../Checkout/Modals/TimeoutModal';
import ResponsiveModal from '../ui2/ResponsiveModal';
import AdderRootContainer from '../reusable/AdderRootContainer';
import AdderContainer from './AdderContainer';
import { ProductUpsellModal } from '@src/domains/products';

const BodySetup = () => {
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const terms = useAppSelector(termsSelector);
  const resendEmail = useAppSelector(resendEmailSelector);
  const { isModalOpened } = useAppSelector(cartStateSelector);
  const currentTermState = useAppSelector(currentTermsSelector);
  const accountCreated = useAppSelector(createdAccountSelector);
  const modifiedPassword = useAppSelector(modifiedPasswordSelector);
  const isRequestTimeout = useAppSelector(isRequestTimeoutSelector);
  const highlightedPA = useAppSelector(highlightedProductAdderSelector);
  const productUpsellModal = useAppSelector(productUpsellModalSelector);
  const multiProductBooking = useAppSelector(multiProductBookingSelector);
  const isAddingToCart = useAppSelector(isAddingToCartSelector);

  const needsPhoneVerification = useAppSelector(buyerNeedsPhoneVerificationSelector);

  const showModalTerms = terms != null && currentTermState != null;
  const {
    errors,
    isPurchaseDisapproved,
    isGatewaysMismatch: isPurchaseGatewaysMismatch,
    isBlockedBuyer: isPurchaseGatewaysBlockedBuyer,
  } = usePaymentErrors();

  const closeRef = React.useRef<() => void>(null);

  const isCheckoutV2 = isFeatureFlagEnabled('PR-3043-checkout');
  const showAccountCreatedSuccessModal = accountCreated && !needsPhoneVerification && !isCheckoutV2;
  const showPasswordModifiedModal = modifiedPassword && !isCheckoutV2;
  const showProductUpsellModal = productUpsellModal !== undefined;
  const isEmbeddedMode = multiProductBooking?.params.values['enable-modal'] === 'false';

  const showsLegacyPaymentError =
    !isCheckoutV2 && (isPurchaseDisapproved || isPurchaseGatewaysMismatch || isPurchaseGatewaysBlockedBuyer);

  const isVisible =
    isModalOpened ||
    highlightedPA != null ||
    (Boolean(multiProductBooking?.isOpen) && !isEmbeddedMode) ||
    showModalTerms ||
    showAccountCreatedSuccessModal ||
    showPasswordModifiedModal ||
    resendEmail ||
    showsLegacyPaymentError;

  const handleClose = () => {
    if (isModalOpened) {
      dispatch(closeCartModal());
      return;
    }

    if (multiProductBooking?.isOpen && !isEmbeddedMode) {
      if (isAddingToCart) return;
      closeRef.current?.();
      return;
    }

    if (closeRef.current) {
      closeRef.current();
    } else if (highlightedPA) {
      dispatch(clearHighlightedProductAdder());
    }
  };

  return (
    <>
      <ResponsiveModal
        open={isRequestTimeout}
        onClose={() => dispatch(setRequestTimeout(false))}
        showCloseButton={false}
        width={552}
      >
        <TimeoutModal />
      </ResponsiveModal>

      {showProductUpsellModal ? (
        <WidgetParamContext.Provider value={productUpsellModal.params}>
          <ProductUpsellModal />
        </WidgetParamContext.Provider>
      ) : null}

      <BackdropModal visible={isVisible} closeModal={handleClose}>
        {!isPurchaseDisapproved &&
          !showModalTerms &&
          !showAccountCreatedSuccessModal &&
          !showPasswordModifiedModal &&
          !resendEmail &&
          !highlightedPA &&
          !multiProductBooking?.isOpen && <CartDetails />}

        {highlightedPA || (multiProductBooking?.isOpen && !isEmbeddedMode) ? (
          <IdProvider id={highlightedPA?.id ?? multiProductBooking!.widgetId}>
            <WidgetParamContext.Provider value={highlightedPA?.params ?? multiProductBooking!.params}>
              <AdderContainer isMultiProductFlow={Boolean(multiProductBooking?.isOpen)}>
                <AdderRootContainer ref={closeRef} />
              </AdderContainer>
            </WidgetParamContext.Provider>
          </IdProvider>
        ) : null}

        {showModalTerms && terms[currentTermState] ? (
          <TermsModal document={terms[currentTermState]!} currentTerm={currentTermState} />
        ) : null}

        {showAccountCreatedSuccessModal ? (
          <SuccessModal
            textInfo={i18n('auth_messageAccountCreatedSuccessFully')}
            textButton={i18n('checkout_continueShopping')}
            handleClose={() => dispatch(setCreatedAccount(false))}
          />
        ) : null}

        {showPasswordModifiedModal ? (
          <SuccessModal
            textInfo={i18n('auth_messagePasswordModifiedSuccessFully')}
            textButton={i18n('auth_login')}
            handleClose={() => dispatch(setmodifiedPassword(false))}
          />
        ) : null}

        {resendEmail && <ResendEmailModal />}

        {/* The redesigned checkout renders its own error states, so these stay for the legacy one only.
            The same flag read drives `isVisible`, so the backdrop never opens over nothing. */}
        {showsLegacyPaymentError && (
          <>
            {isPurchaseDisapproved && <DisapprovedPurchaseModal descriptions={errors['transactionError']} />}

            {isPurchaseGatewaysMismatch && <GatewayPaymentErrorModal />}

            {isPurchaseGatewaysBlockedBuyer && (
              <GatewayPaymentErrorModal
                title={i18n('checkout_payment_gateway_blocked_buyer_error_title')}
                helperText={i18n('checkout_payment_gateway_blocked_buyer_error_helper_text')}
              />
            )}
          </>
        )}
      </BackdropModal>
    </>
  );
};

export default BodySetup;
